import { Check, ChevronDown, Store } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/cn";
import {
  hierarchyRepository,
  type SectionOption,
} from "@/repositories/hierarchyRepository";
import { useAuthStore } from "@/stores/authStore";

/**
 * The Branch ▸ Section control from the designs.
 *
 * It shows only what the signed-in user may operate — the list comes from the
 * server already filtered by their role assignments, so a butchery cashier sees
 * one entry and an owner sees all of them.
 *
 * **Nothing selected means everything**, and the label has to say so. An earlier
 * version fell back to `options[0]` for display while selecting nothing, so an
 * owner's top bar read "Maseko Spaza — Vosloorus" while every figure on the
 * screen covered the whole business. The control was describing a filter it had
 * not applied. Choosing an entry sets the scope headers and genuinely narrows;
 * choosing "All branches" clears them.
 *
 * When a user may operate exactly one counter there is nothing to choose, so the
 * control is inert and simply names where they are — which is why the ninety per
 * cent of merchants with one counter never meet the concept at all.
 */
export function BranchSelector() {
  const session = useAuthStore((s) => s.session);
  const setActiveScope = useAuthStore((s) => s.setActiveScope);

  const [options, setOptions] = useState<SectionOption[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!session) return;
    void hierarchyRepository.sectionsForUser().then(setOptions);
  }, [session?.user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onClickAway = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [open]);

  if (!session || options.length === 0) return null;

  // `null` is a real state, not a missing one: it means "everything I may see".
  const active =
    options.find((option) => option.sectionId === session.activeSectionId) ??
    null;
  const choosable = options.length > 1;

  const branches = new Set(options.map((option) => option.branchId));
  const everythingLabel = branches.size > 1 ? "All branches" : "All counters";

  const label = active
    ? branches.size > 1
      ? `${active.branchName} · ${active.sectionName}`
      : active.sectionName
    : choosable
      ? everythingLabel
      : (options[0]?.branchName ?? "");

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => choosable && setOpen((value) => !value)}
        disabled={!choosable}
        aria-haspopup={choosable ? "listbox" : undefined}
        aria-expanded={choosable ? open : undefined}
        className={cn(
          "flex h-touch items-center gap-2.5 rounded-control border border-line bg-surface px-3 text-sm text-ink-900",
          choosable ? "hover:bg-canvas" : "cursor-default",
        )}
      >
        <Store className="size-4 shrink-0 text-brand-500" aria-hidden />
        <span className="max-w-52 truncate">{label}</span>
        {choosable && (
          <ChevronDown className="size-4 shrink-0 text-ink-400" aria-hidden />
        )}
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Choose a branch and section"
          className="absolute right-0 z-20 mt-1 max-h-80 w-[min(18rem,calc(100vw-1.5rem))] overflow-y-auto rounded-card border border-line bg-surface py-1 shadow-pop"
        >
          <li>
            <Option
              selected={active === null}
              title={everythingLabel}
              subtitle="Everything you can see"
              onClick={() => {
                void setActiveScope(null, null);
                setOpen(false);
              }}
            />
          </li>

          {options.map((option) => (
            <li key={option.sectionId}>
              <Option
                selected={option.sectionId === active?.sectionId}
                title={option.sectionName}
                subtitle={option.branchName}
                onClick={() => {
                  void setActiveScope(option.branchId, option.sectionId);
                  setOpen(false);
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Option({
  selected,
  title,
  subtitle,
  onClick,
}: {
  selected: boolean;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-canvas"
    >
      <Check
        className={cn(
          "size-4 shrink-0",
          selected ? "text-brand-500" : "opacity-0",
        )}
        aria-hidden
      />
      <span className="min-w-0">
        <span className="block truncate font-medium text-ink-900">{title}</span>
        <span className="block truncate text-xs text-ink-500">{subtitle}</span>
      </span>
    </button>
  );
}
